import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { Plan } from '@/constants/enum';
import { checkShowErrorInline, handleToastMutation } from '@/helpers';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import toastSlice from '@/redux/slice/toast.slice';
import { Button, DropZone, Icon, Text } from '@shopify/polaris';
import { PageAttachmentIcon } from '@shopify/polaris-icons';
import { ReactNode, memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BaseModal from '../BaseModal';
import { StyledUploadedFiles } from './styled';

interface IImportCSV {
  title: string;
  content?: ReactNode;
  onImport: (file: File) => Promise<any>;
  importing: boolean;
  importLabel?: string;
  cancelLabel?: string;
  buttonLabel?: string;
}

function ImportCSV({
  title,
  content,
  onImport,
  importing,
  importLabel = 'Import',
  cancelLabel = 'Cancel',
  buttonLabel = 'Import CSV',
}: IImportCSV) {
  const dispatch = useDispatch();
  const dataSettings = useSelector(dataSettingsSelector);
  const isDisabledContent = dataSettings.plan === Plan.FREE || dataSettings.plan === Plan.STANDARD;

  const [active, setActive] = useState(false);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [isShowErrorInline, setIsShowErrorInline] = useState({ status: false, msg: '' });

  const toggleModal = useCallback(() => {
    setFile(null);
    setActive(!active);
    setIsShowErrorInline({ status: false, msg: '' });
  }, [active]);

  const validateFile = (file: any) => {
    if (file.size > 5000000) {
      setIsShowErrorInline({ status: true, msg: 'File too large, maximum file size is 5MB' });
    } else {
      setIsShowErrorInline({ status: false, msg: '' });
    }
    return file.size < 5000000;
  };

  const activator = <Button onClick={toggleModal}>{buttonLabel}</Button>;
  const [file, setFile] = useState<File | null>(null);

  const toggleOpenFileDialog = useCallback(() => setOpenFileDialog((openFileDialog) => !openFileDialog), []);

  const handleDropZoneDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => setFile(acceptedFiles[0]),
    [],
  );
  const fileUpload = !file && <DropZone.FileUpload actionHint="Accept only .csv and .xlsx. Maximum size: 5MB" />;
  const uploadedFiles = file && (
    <StyledUploadedFiles>
      <div className="thumbnail">
        <Icon source={PageAttachmentIcon} />
      </div>
      <div className="content">
        <Text as="p" fontWeight="bold">
          {file.name}
        </Text>
        <Button onClick={toggleOpenFileDialog}>Replace</Button>
      </div>
    </StyledUploadedFiles>
  );
  const handleImport = async () => {
    if (onImport && file) {
      const status = await onImport(file);
      const checkError = checkShowErrorInline(status);
      if (checkError.status) {
        setIsShowErrorInline(checkError);
        setFile(null);
      } else {
        dispatch(toastSlice.actions.handleToast(handleToastMutation(status)));
        toggleModal();
      }
    }
  };
  return (
    <BaseModal
      activator={activator}
      open={active}
      onClose={toggleModal}
      title={title}
      primaryAction={{
        content: importLabel,
        onAction: handleImport,
        loading: importing,
        disabled: importing || !file,
      }}
      secondaryActions={[
        {
          content: cancelLabel,
          onAction: toggleModal,
          disabled: importing,
        },
      ]}
    >
      {isDisabledContent ? (
        <div className="mb-8">
          <UpgradeTextWithTouchPoint text="Pro" />
        </div>
      ) : null}
      <div className={isDisabledContent ? 'disable-content' : ''}>
        <Text as={'p'}>
          Download a <a href={`${process.env.REACT_APP_API_END_POINT}file/rule/template`}>sample template</a> to see an example of
          the format required.
          <br />
          Instructions are included in the file.
          <br />
          Unsupported conditions: Exceptions, States.
        </Text>
        <div className="mt-8" style={{ height: `${file ? 0 : 'auto'}`, overflow: 'hidden' }}>
          <DropZone
            customValidator={validateFile}
            openFileDialog={openFileDialog}
            allowMultiple={false}
            accept={'.xlsx, .csv'}
            type={'file'}
            onDrop={handleDropZoneDrop}
            onFileDialogClose={toggleOpenFileDialog}
            disabled={isDisabledContent}
          >
            {fileUpload}
          </DropZone>
          {isShowErrorInline.status ? (
            <Text as="span" variant="bodySm" tone="critical">
              {isShowErrorInline.msg}
            </Text>
          ) : null}
        </div>
        {uploadedFiles}
        {content}
      </div>
    </BaseModal>
  );
}

export default memo(ImportCSV);
